<template>
  <RevForm
    :id="FORM_ID"
    class="pl-16"
    :initial-values="initialFormValues"
    :validate
    @submit="handleSubmit"
  >
    <template #default="{ reset, errors, values }">
      <RevDrawer :name="MODAL_NAME" :title="formattedModalTitle" @open="reset">
        <template #body>
          <RevLoadingScreen
            v-if="isLoading"
            class="mt-20 h-full w-full self-center"
            :text="formattedLoadingMessage"
            variant="light"
          />
          <template v-if="!isLoading">
            <RevInputSelect
              id="taxIdentificationNumberCountry"
              v-model="values.country"
              class="sm:mb-12 sm:w-full md:mb-24"
              :error="errors.country"
              :label="i18n(translations.taxIdentificationNumberCountryLabel)"
              :options="formattedTaxIdentificationNumberCountryOptions"
            />

            <RevInputText
              id="taxIdentificationNumber"
              v-model="values.taxIdentificationNumber"
              :error="errors.taxIdentificationNumber"
              :label="i18n(translations.taxIdentificationNumberLabel)"
              type="text"
            />
          </template>
        </template>

        <template #footer="{ close }">
          <RevButton
            id="editTaxIdentificationNumberButton"
            class="mt-12"
            :form="FORM_ID"
            full-width="always"
            type="submit"
            variant="primary"
          >
            {{ i18n(translations.submitButton) }}
          </RevButton>
          <RevLink id="cancel_form" class="m-auto mt-16 w-full" @click="close">
            {{ i18n(translations.cancelButton) }}
          </RevLink>
        </template>
      </RevDrawer>
    </template>
  </RevForm>
</template>

<script setup lang="ts">
import { getApiErrorMessage } from '#imports'
import { computed, ref } from 'vue'

import { type Country, sellerProfileAPI } from '@backmarket/http-api'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevDrawer } from '@ds/components/Drawer'
import { RevForm, makeValidate, required } from '@ds/components/Form'
import { RevInputSelect, type Value } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'
import { RevLink } from '@ds/components/Link'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { closeModal } from '@ds/components/ModalBase'

import type { Error } from '~/utils/apiError'

import { MODAL_NAMES } from '../../../../../Profile.constants'
import translations from '../TaxIdentificationNumber.translations'

type TaxIdentificationNumberFormValues = {
  country: Value | Country
  taxIdentificationNumber: string
}

const i18n = useI18n()
const { openErrorToast, openSuccessToast } = useTheToast()
const emit = defineEmits(['tin-updated'])

const NEW_TAX_IDENTIFICATION_NUMBER = {
  id: null,
  country: '',
  taxIdentificationNumber: '',
  isMain: false,
}
const FORM_ID = 'edit_tax_identification_number_form'
const MODAL_NAME = MODAL_NAMES.EDIT_TAX_IDENTIFICATION_NUMBER

const props = defineProps<{
  alreadyAddedCountries: Country[] | undefined
  taxIdentificationNumberCountryOptions: Country[]
  taxIdentificationNumberToEdit: sellerProfileAPI.GetTaxIdentificationNumbersItem | null
}>()

const isLoading = ref(false)

const validate = makeValidate<TaxIdentificationNumberFormValues>({
  country: [required(i18n(translations.countryRequired))],
  taxIdentificationNumber: [
    required(i18n(translations.taxIdentificationNumberRequired)),
  ],
})

const formattedModalTitle = computed(() => {
  return props.taxIdentificationNumberToEdit?.id
    ? i18n(translations.editTinModalTitle)
    : i18n(translations.addTinModalTitle)
})

const formattedLoadingMessage = computed(() => {
  return props.taxIdentificationNumberToEdit?.id
    ? i18n(translations.loadingEditTinModalTitle)
    : i18n(translations.loadingAddTinModalTitle)
})

const initialFormValues = computed(() => {
  return props.taxIdentificationNumberToEdit ?? NEW_TAX_IDENTIFICATION_NUMBER
})

const formattedTaxIdentificationNumberCountryOptions = computed(() => {
  return props.taxIdentificationNumberCountryOptions
    ?.filter((country) => {
      return (
        !props.alreadyAddedCountries?.includes(country) ||
        country === props.taxIdentificationNumberToEdit?.country
      )
    })
    .map((country) => {
      return {
        label: i18n.country(country),
        value: country,
      }
    })
})

async function handleSubmit(values: TaxIdentificationNumberFormValues) {
  isLoading.value = true

  let requestPayload
  let requestType
  let successTitle

  const requestBody = {
    country: values.country,
    taxIdentificationNumber: values.taxIdentificationNumber,
  }

  if (props.taxIdentificationNumberToEdit?.id) {
    requestType = sellerProfileAPI.updateTaxIdentificationNumbers

    requestPayload = {
      pathParams: { tinId: props.taxIdentificationNumberToEdit?.id },
      body: requestBody,
    }

    successTitle = i18n(translations.taxIdentificationNumberUpdateSuccessTitle)
  } else {
    requestType = sellerProfileAPI.createTaxIdentificationNumber

    requestPayload = {
      body: requestBody,
    }

    successTitle = i18n(translations.taxIdentificationNumberCreateSuccessTitle)
  }

  try {
    await $httpFetch(requestType, {
      ...requestPayload,
    })

    openSuccessToast({
      title: successTitle,
      content: i18n(translations.taxIdentificationNumberModalSuccessContent),
    })

    emit('tin-updated')

    closeModal(MODAL_NAME)
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as Error),
    })
  } finally {
    isLoading.value = false
  }
}
</script>
